import {
  Avatar,
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  HStack,
  Icon,
  IconButton,
  Link,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { FiCalendar, FiExternalLink, FiInfo, FiTarget, FiX } from "react-icons/fi";
import { IconType } from "react-icons";
import { useState } from "react";
import { titleize } from "helpers/string";
import { dateInFormat } from "helpers/time";
import CDS, { AdminMeetingTypeTag } from "design-system";
import useConfirmation from "hooks/useConfirmation";
import EmbeddedHtml from "components/DataDisplay/EmbeddedHtml";
import UserRoleOrEmailMultiSelect, {
  UserRoleOrEmailOption,
} from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import { Meeting, MeetingAttendee, MeetingResponseStatus, MeetingType } from "features/Resources/Meeting/models";
import {
  useAddAttendeeToMeeting,
  useMeetingDetailQuery,
  useRemoveAttendeeFromMeeting,
} from "features/Resources/Meeting/hooks";

interface MeetingDrawerProps {
  meeting: Meeting | null;
  onClose: () => void;
  /** Journey's new-hire user id — that attendee can't be removed. */
  newHireId?: string;
}

function meetingTypeLabel(type: MeetingType): string {
  if (type === "individual") return "1:1 meeting";
  if (type === "group_session_recurring") return "Group session · standing";
  return "Group session · one-time";
}

const RSVP: Record<MeetingResponseStatus, { label: string; color: string; filled: boolean }> = {
  accepted: { label: "Accepted", color: "green.600", filled: true },
  declined: { label: "Declined", color: "red.600", filled: true },
  tentative: { label: "Tentative", color: "orange.500", filled: true },
  pending: { label: "No reply", color: "fg.muted", filled: false },
};

function Pill({ label, colorScheme }: { label: string; colorScheme: string }) {
  return (
    <Box px={1.5} py={0.5} borderRadius="sm" bg={`${colorScheme}.50`}>
      <CDS.Text10 fontWeight="bold" color={`${colorScheme}.700`} letterSpacing="0.04em">
        {label.toUpperCase()}
      </CDS.Text10>
    </Box>
  );
}

function Rsvp({ status }: { status: MeetingResponseStatus }) {
  const { label, color, filled } = RSVP[status] ?? RSVP.pending;
  return (
    <HStack spacing={1.5} flexShrink={0}>
      <Box w="8px" h="8px" borderRadius="full" bg={filled ? color : "transparent"} border="1.5px solid" borderColor={color} />
      <CDS.Text12 color={color} fontWeight="medium">
        {label}
      </CDS.Text12>
    </HStack>
  );
}

function Section({ icon, label, html }: { icon: IconType; label: string; html?: string }) {
  if (!html) return null;
  return (
    <Box>
      <HStack spacing={2} mb={2}>
        <Icon as={icon} color="blue.500" boxSize={3.5} />
        <CDS.Text12 fontWeight="bold" color="fg.muted" letterSpacing="0.06em">
          {label}
        </CDS.Text12>
      </HStack>
      <Box color="fg.default">
        <EmbeddedHtml innerHtml={html} />
      </Box>
    </Box>
  );
}

function InviteRow({
  attendee,
  isNewHire,
  onRemove,
}: {
  attendee: MeetingAttendee;
  isNewHire: boolean;
  onRemove: () => void;
}) {
  const name = attendee.user?.fullName ?? attendee.emailAddress;
  return (
    <HStack spacing={3} py={2.5} align="center">
      <Avatar size="sm" name={name} src={attendee.user?.avatarUrl} />
      <Box flex={1} minW={0}>
        <HStack spacing={2} align="center" wrap="wrap">
          <CDS.Text14 fontWeight="semibold">{name}</CDS.Text14>
          {isNewHire && <Pill label="New hire" colorScheme="blue" />}
          {!isNewHire && attendee.role && <Pill label={titleize(attendee.role)} colorScheme="gray" />}
          {attendee.isLead && <Pill label="Lead" colorScheme="purple" />}
        </HStack>
        {attendee.emailAddress && (
          <CDS.Text12 color="fg.muted" noOfLines={1}>
            {attendee.emailAddress}
          </CDS.Text12>
        )}
      </Box>
      <Rsvp status={attendee.responseStatus} />
      {!isNewHire && attendee.userId && (
        <IconButton
          aria-label={`Remove ${name}`}
          icon={<Icon as={FiX} />}
          size="xs"
          variant="ghost"
          color="fg.muted"
          onClick={onRemove}
        />
      )}
    </HStack>
  );
}

export default function MeetingDrawer({ meeting, onClose, newHireId }: MeetingDrawerProps) {
  const [showAdd, setShowAdd] = useState(false);
  const { data: detail } = useMeetingDetailQuery(meeting?.id, { enabled: !!meeting });
  const resolved = detail ?? meeting;

  const { mutate: addAttendee } = useAddAttendeeToMeeting(resolved?.id ?? "");
  const { mutate: removeAttendee } = useRemoveAttendeeFromMeeting(resolved?.id ?? "");
  const { confirm, ConfirmationDialog } = useConfirmation();

  const requestRemove = (attendee: MeetingAttendee) => {
    const name = attendee.user?.fullName ?? attendee.emailAddress;
    confirm({
      header: "Remove attendee",
      body: `Remove ${name} from this meeting? They'll be dropped from the calendar invite.`,
      confirmButtonText: "Remove",
      intent: "warning",
      onConfirm: () => removeAttendee({ userId: attendee.userId }),
    });
  };

  const attendees = resolved?.attendees ?? [];
  const isGroup = resolved?.meetingType !== "individual";
  const shown = new Set(attendees.map((a) => a.userId || a.emailAddress));
  const extraCount = isGroup
    ? (resolved?.allSessionAttendees ?? []).filter((sa) => !shown.has(sa.userId || sa.emailAddress)).length
    : 0;
  const invited = attendees.length + extraCount;
  const required = attendees.filter((a) => a.invitationType === "required").length;

  const handleAdd = (selected: UserRoleOrEmailOption[]) => {
    selected.forEach((option) => {
      if (option.recordId) addAttendee({ userId: option.recordId });
      else if (option.email) addAttendee({ email: option.email, emailType: option.emailType });
    });
    setShowAdd(false);
  };

  return (
    <Drawer isOpen={!!meeting} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton mt="10px" />
        <DrawerBody pt={8}>
          {resolved && (
            <VStack align="stretch" spacing={5}>
              <Box>
                <CDS.Heading22>{resolved.name}</CDS.Heading22>
                <HStack spacing={2} mt={2}>
                  <AdminMeetingTypeTag.IconBadge meetingType={resolved.meetingType} />
                  <CDS.Text14 color="fg.muted">{meetingTypeLabel(resolved.meetingType)}</CDS.Text14>
                </HStack>
                {resolved.startTime && (
                  <HStack spacing={2} mt={2}>
                    <Icon as={FiCalendar} color="fg.muted" boxSize={3.5} />
                    <CDS.Text14 color="fg.subtle">
                      {dateInFormat(resolved.startTime, "dddd, MMM D, YYYY · h:mm A", resolved.timezone)}
                    </CDS.Text14>
                  </HStack>
                )}
              </Box>

              <Divider />

              <Section
                icon={FiTarget}
                label="LEARNING OBJECTIVE"
                html={resolved.learningObjectiveDisplay ?? resolved.learningObjective}
              />
              <Section icon={FiInfo} label="PURPOSE" html={resolved.purposeDisplay ?? resolved.purpose} />
              <Section
                icon={FiInfo}
                label="ADDITIONAL INFO"
                html={resolved.additionalInfoDisplay ?? resolved.additionalInfo}
              />

              <Box>
                <HStack justify="space-between" align="baseline" mb={1}>
                  <CDS.Text12 fontWeight="bold" color="fg.muted" letterSpacing="0.06em">
                    INVITE LIST
                  </CDS.Text12>
                  <CDS.Text12 color="fg.subtle">
                    {invited} invited · {required} required
                  </CDS.Text12>
                </HStack>
                <VStack align="stretch" spacing={0} divider={<Divider />}>
                  {attendees.map((attendee) => (
                    <InviteRow
                      key={attendee.id}
                      attendee={attendee}
                      isNewHire={!!newHireId && attendee.userId === newHireId}
                      onRemove={() => requestRemove(attendee)}
                    />
                  ))}
                  {extraCount > 0 && (
                    <HStack spacing={3} py={2.5} align="center">
                      <Avatar size="sm" name={`+ ${extraCount}`} bg="gray.100" color="fg.muted" getInitials={(v) => v} />
                      <Box flex={1} minW={0}>
                        <HStack spacing={2} align="center">
                          <CDS.Text14 fontWeight="semibold">
                            + {extraCount} more {extraCount === 1 ? "invitee" : "invitees"}
                          </CDS.Text14>
                          <Pill label="Attendees" colorScheme="gray" />
                        </HStack>
                        <CDS.Text12 color="fg.muted">group session</CDS.Text12>
                      </Box>
                    </HStack>
                  )}
                </VStack>

                {showAdd && (
                  <Box mt={2}>
                    <UserRoleOrEmailMultiSelect value={[]} onChange={handleAdd} includeRole={false} includePersonal />
                  </Box>
                )}
              </Box>

              {isGroup && (
                <HStack spacing={2} align="start">
                  <Icon as={FiCalendar} color="fg.subtle" boxSize={3.5} mt="2px" />
                  <CDS.Text12 color="fg.subtle">
                    Attendees & RSVPs mirror the Google Calendar event. Google is the source of truth — edits made there
                    override this view.
                  </CDS.Text12>
                </HStack>
              )}
            </VStack>
          )}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" justifyContent="flex-start">
          <HStack spacing={3}>
            <CDS.Button intent={CDS.Button.intent.indigo} size={CDS.Button.size.md} onClick={() => setShowAdd((v) => !v)}>
              Add attendee
            </CDS.Button>
            <Tooltip label="Coming soon" placement="top" hasArrow>
              <CDS.Button
                intent={CDS.Button.intent.tertiary}
                variant={CDS.Button.variant.outline}
                size={CDS.Button.size.md}
                disabled
              >
                Message attendees
              </CDS.Button>
            </Tooltip>
          </HStack>
          {resolved?.externalUrl && (
            <Link href={resolved.externalUrl} isExternal ml="auto">
              <HStack spacing={1}>
                <CDS.Text14 color="blue.600" fontWeight="medium">
                  Open in Calendar
                </CDS.Text14>
                <Icon as={FiExternalLink} color="blue.600" boxSize={3.5} />
              </HStack>
            </Link>
          )}
        </DrawerFooter>
        <ConfirmationDialog />
      </DrawerContent>
    </Drawer>
  );
}
